import { apiClient } from '@lib/api/client';
import type { CertificateListResponse, JwtProcessType } from '../types/certificate.types';
import { appInsightsService } from '../../../services/appInsights/appInsights.service';

export const certificateService = {
  async getCertificates(): Promise<CertificateListResponse> {
    const response = await apiClient.get<any>('/companies/client-certificates');

    // Transform response to match expected format
    // The API returns: { hasCertificates, certificates: [...], status }
    // We need to return the first certificate if it exists
    const certificate = response.data.certificates?.[0] || null;

    const transformedData = {
      certificate: certificate ? {
        certificateId: certificate.CertificateId,
        companyId: certificate.CompanyId,
        certificateAuth: certificate.Certificate_Auth,
        certificateSig: certificate.Certificate_Sig,
        secretJwt: certificate.Secret_Jwt,
        certificateKey: certificate.Certificate_Key,
        blueprintSign: certificate.Blueprint_Sign,
        blueprintAuth: certificate.Blueprint_Auth,
        createdDate: certificate.CreatedDate,
        lastModifiedDate: certificate.LastModifiedDate,
        status: certificate.Status,
        authCertExpiresAt: certificate.AuthCertExpiresAt,
        signCertExpiresAt: certificate.SignCertExpiresAt,
      } : null
    };
    return transformedData as CertificateListResponse;
  },

  /**
   * Upload certificates to PAC (DGI)
   * Converts .pfx/.p12 files to base64 and extracts names
   */
  async uploadCertificates(data: {
    authCertFile: File;
    signCertFile: File;
    secretJwt: string | null;
    certificatePassword: string;
    secretDGI?: string;
  }): Promise<any> {
    // Helper function to convert file to base64
    const fileToBase64 = (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          // Remove the data:application/x-pkcs12;base64, prefix
          const base64 = (reader.result as string).split(',')[1];
          resolve(base64);
        };
        reader.onerror = error => reject(error);
      });
    };

    // Helper function to extract certificate name (without extension)
    const extractCertName = (filename: string): string => {
      return filename.replace(/\.(pfx|p12)$/i, '');
    };

    try {

      const [authBase64, signBase64] = await Promise.all([
        fileToBase64(data.authCertFile),
        fileToBase64(data.signCertFile),
      ]);

      const payload = {
        authCertificate: {
          name: extractCertName(data.authCertFile.name),
          base64: authBase64,
        },
        signCertificate: {
          name: extractCertName(data.signCertFile.name),
          base64: signBase64,
        },
        secretJwt: data.secretJwt,
        certificatePassword: data.certificatePassword,
        secretDGI: data.secretDGI || null,
      };

      const response = await apiClient.post('/certificates/upload', payload);
      return response.data;
    } catch (error) {
      appInsightsService.trackException(error, { context: 'src/features/certificates/services/certificateService' });
      throw error;
    }
  },

  /**
   * Update certificate affiliation: PAC affiliation, certificate renewal and JWT create/update.
   * Converts .pfx/.p12 files to base64 and extracts names, same as uploadCertificates.
   */
  async updateCertificateAffiliation(data: {
    // Opcionales: igual que el portal legacy, se puede afiliar al PAC o crear/actualizar
    // el JWT sin volver a cargar certificados (upDateCert = false).
    authCertFile?: File | null;
    signCertFile?: File | null;
    certificatePassword?: string;
    updateCertificates: boolean;
    affiliationPac: boolean;
    onlySignatureCert: boolean;
    jwtProcessType: JwtProcessType;
    secretJwt?: string | null;
  }): Promise<any> {
    // Helper function to convert file to base64
    const fileToBase64 = (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          // Remove the data:application/x-pkcs12;base64, prefix
          const base64 = (reader.result as string).split(',')[1];
          resolve(base64);
        };
        reader.onerror = error => reject(error);
      });
    };

    // Helper function to extract certificate name (without extension)
    const extractCertName = (filename: string): string => {
      return filename.replace(/\.(pfx|p12)$/i, '');
    };

    const authFile = data.updateCertificates ? data.authCertFile ?? null : null;
    const signFile = data.updateCertificates ? data.signCertFile ?? null : null;

    const [authBase64, signBase64] = await Promise.all([
      authFile ? fileToBase64(authFile) : Promise.resolve(null),
      signFile ? fileToBase64(signFile) : Promise.resolve(null),
    ]);

    const payload = {
      authCertificate: authFile && authBase64
        ? { name: extractCertName(authFile.name), base64: authBase64 }
        : null,
      signCertificate: signFile && signBase64
        ? { name: extractCertName(signFile.name), base64: signBase64 }
        : null,
      certificatePassword: data.updateCertificates ? data.certificatePassword || null : null,
      updateCertificates: data.updateCertificates,
      affiliationPac: data.affiliationPac,
      onlySignatureCert: data.onlySignatureCert,
      jwtProcessType: data.jwtProcessType,
      secretJwt: data.jwtProcessType === 'update' ? (data.secretJwt || null) : null,
    };

    const response = await apiClient.post('/certificates/affiliation', payload);
    return response.data;
  },
};
